import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../constants/config';

// Async thunk for fetching all applications
export const fetchApplications = createAsyncThunk(
  'application/fetchApplications',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      // Build query string from filters
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

      const response = await fetch(`${config.APPLICATIONS_BASE}${queryString}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching a specific application
export const fetchApplication = createAsyncThunk(
  'application/fetchApplication',
  async (applicationId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.APPLICATIONS_BASE}/${applicationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for creating a new application
export const createApplication = createAsyncThunk(
  'application/createApplication',
  async (applicationData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(config.CREATE_APPLICATION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(applicationData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for updating an application
export const updateApplication = createAsyncThunk(
  'application/updateApplication',
  async ({ applicationId, applicationData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.APPLICATIONS_BASE}/${applicationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(applicationData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for deleting an application
export const deleteApplication = createAsyncThunk(
  'application/deleteApplication',
  async (applicationId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.APPLICATIONS_BASE}/${applicationId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      return { applicationId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching opportunity applications
export const fetchOpportunityApplications = createAsyncThunk(
  'application/fetchOpportunityApplications',
  async (opportunityId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.GET_OPPORTUNITY_APPLICATIONS.replace(':opportunityId', opportunityId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return { opportunityId, applications: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching actor applications
export const fetchActorApplications = createAsyncThunk(
  'application/fetchActorApplications',
  async (actorId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return rejectWithValue('No authentication token found');
      }

      const response = await fetch(`${config.GET_ACTOR_APPLICATIONS.replace(':actorId', actorId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData);
      }

      const data = await response.json();
      return { actorId, applications: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  applications: [],
  selectedApplication: null,
  opportunityApplications: {},
  actorApplications: {},
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  filters: {
    status: null
  }
};

// Create the slice
const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    clearSelectedApplication: (state) => {
      state.selectedApplication = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchApplications
      .addCase(fetchApplications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.applications = action.payload;
        state.error = null;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch applications';
      })

      // Handle fetchApplication
      .addCase(fetchApplication.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchApplication.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedApplication = action.payload;
        state.error = null;
      })
      .addCase(fetchApplication.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch application';
      })

      // Handle createApplication
      .addCase(createApplication.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createApplication.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.applications.push(action.payload);
        state.selectedApplication = action.payload;

        // Update actor applications if we have them loaded
        if (action.payload.actor && state.actorApplications[action.payload.actor.uuid]) {
          state.actorApplications[action.payload.actor.uuid].push(action.payload);
        }

        // Update opportunity applications if we have them loaded
        if (action.payload.opportunity && state.opportunityApplications[action.payload.opportunity.uuid]) {
          state.opportunityApplications[action.payload.opportunity.uuid].push(action.payload);
        }

        state.error = null;
      })
      .addCase(createApplication.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to create application';
      })

      // Handle updateApplication
      .addCase(updateApplication.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        state.status = 'succeeded';

        // Update in main applications array
        const index = state.applications.findIndex(app => app.uuid === action.payload.uuid);
        if (index !== -1) {
          state.applications[index] = action.payload;
        }

        // Update in actor applications if present
        if (action.payload.actor && state.actorApplications[action.payload.actor.uuid]) {
          const actorAppIndex = state.actorApplications[action.payload.actor.uuid]
            .findIndex(app => app.uuid === action.payload.uuid);
          if (actorAppIndex !== -1) {
            state.actorApplications[action.payload.actor.uuid][actorAppIndex] = action.payload;
          }
        }

        // Update in opportunity applications if present
        if (action.payload.opportunity && state.opportunityApplications[action.payload.opportunity.uuid]) {
          const oppAppIndex = state.opportunityApplications[action.payload.opportunity.uuid]
            .findIndex(app => app.uuid === action.payload.uuid);
          if (oppAppIndex !== -1) {
            state.opportunityApplications[action.payload.opportunity.uuid][oppAppIndex] = action.payload;
          }
        }

        state.selectedApplication = action.payload;
        state.error = null;
      })
      .addCase(updateApplication.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update application';
      })

      // Handle deleteApplication
      .addCase(deleteApplication.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteApplication.fulfilled, (state, action) => {
        state.status = 'succeeded';

        // Remove from main applications array
        state.applications = state.applications.filter(
          app => app.uuid !== action.payload.applicationId
        );

        // Remove from actor applications if present
        Object.keys(state.actorApplications).forEach(actorId => {
          state.actorApplications[actorId] = state.actorApplications[actorId].filter(
            app => app.uuid !== action.payload.applicationId
          );
        });

        // Remove from opportunity applications if present
        Object.keys(state.opportunityApplications).forEach(oppId => {
          state.opportunityApplications[oppId] = state.opportunityApplications[oppId].filter(
            app => app.uuid !== action.payload.applicationId
          );
        });

        if (state.selectedApplication && state.selectedApplication.uuid === action.payload.applicationId) {
          state.selectedApplication = null;
        }

        state.error = null;
      })
      .addCase(deleteApplication.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to delete application';
      })

      // Handle fetchOpportunityApplications
      .addCase(fetchOpportunityApplications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchOpportunityApplications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.opportunityApplications[action.payload.opportunityId] = action.payload.applications;
        state.error = null;
      })
      .addCase(fetchOpportunityApplications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch opportunity applications';
      })

      // Handle fetchActorApplications
      .addCase(fetchActorApplications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchActorApplications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.actorApplications[action.payload.actorId] = action.payload.applications;
        state.error = null;
      })
      .addCase(fetchActorApplications.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch actor applications';
      });
  }
});

// Export actions
export const { clearSelectedApplication, setFilters, clearFilters } = applicationSlice.actions;

// Export selectors
export const selectApplications = (state) => state.user.application.applications;
export const selectSelectedApplication = (state) => state.user.application.selectedApplication;
export const selectApplicationsStatus = (state) => state.user.application.status;
export const selectApplicationsError = (state) => state.user.application.error;
export const selectApplicationFilters = (state) => state.user.application.filters;

// React-Redux v8+ in dev mode warns when a selector returns a different
// reference for the same input ("Selector unknown returned a different
// result..."). The two factory selectors below previously did
//   state.user.application.actorApplications[actorId] || []
// which materialized a *fresh* `[]` on every call when the per-actor /
// per-opportunity slot was missing, causing the warning to fire any time
// an unrelated store update re-ran the selector. Use a single shared
// EMPTY_ARRAY sentinel so the empty case is now reference-stable.
// Strict-equality safe: the array stays frozen in dev to make accidental
// mutations crash loudly rather than silently succeed.
const EMPTY_APPLICATIONS_ARRAY = Object.freeze([]);

export const selectOpportunityApplications = (opportunityId) => (state) =>
  state.user.application.opportunityApplications[opportunityId] || EMPTY_APPLICATIONS_ARRAY;
export const selectActorApplications = (actorId) => (state) =>
  state.user.application.actorApplications[actorId] || EMPTY_APPLICATIONS_ARRAY;

// Export reducer
export default applicationSlice.reducer;
